'use client';

import { c as _c } from "react/compiler-runtime";
import type { UseCodeSessionResult } from '@/app/(home)/code/_prototype/use-code-session';
import type { SidebarChatEngine, SidebarChatToolCallEvent, SidebarChatTurnEnd } from '@/components/chat/sidebar-chat-engine';
import type { AiContext } from '@/components/chat/types/ai-context';
import type { SidebarChatMessage } from '@/components/chat/types/sidebar-chat-message';
import type { TurnActivity } from '@/data/code-sessions/turn-activity';
import { useQueryClient } from '@tanstack/react-query';
import type { ChatStatus } from 'ai';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useEventCallback } from 'usehooks-ts';
import type { SandboxStartStatus } from './sandbox-start-status';
import { sandboxStartStatus } from './sandbox-start-status';
import { PAGE_ACTION_TOOL_NAMES, sessionChatMessages } from './session-chat-messages';
import { useCodeSession } from '@/app/(home)/code/_prototype/use-code-session';
import { markPageActionsReplayed, pageActionReplayKey } from '@/components/chat/tools/page-action-replays';
import { prepareSidebarChatRequestBody } from '@/components/chat/types/chat-request';
import { sendPageChatMessage } from '@/data/chat/page-chat-session';
import { codeSessionKeys } from '@/data/code-sessions/queries';
import { isTerminalActivity } from '@/data/code-sessions/turn-activity';
import { deterministicPromptId } from '@/lib/code-sessions/turn-utils';
import { usePrStore } from '@/lib/stores/pr-store';

/** A message the person sent that the transcript does not show yet. */
type PendingPageChatPrompt = {
  /** The turn it sends; null for the message that starts the session, whose turn cubic names. */
  turnId: string | null;
  text: string;
};

/** A session not read yet has no turns: one empty list, so effects reading it do not rerun. */
const NO_ACTIVITY: readonly TurnActivity[] = [];
type ChatSend = (input: {
  text: string;
  turnId: string;
  start: boolean;
  model: string | undefined;
  delivery: 'queue' | 'steer';
}) => Promise<void>;

/**
 * Sends a message to the page's chat session as the page sees it now (`sendPageChatMessage`),
 * with the page's AI context as the sidebar's own chat sends it.
 */
function usePageChatSend(t0) {
  const $ = _c(5);
  const {
    sessionId,
    resolveRequestContext
  } = t0;
  const {
    installationId,
    pullRequestId
  } = usePrStore();
  let t1;
  if ($[0] !== installationId || $[1] !== pullRequestId || $[2] !== resolveRequestContext || $[3] !== sessionId) {
    t1 = async t2 => {
      const {
        text,
        turnId,
        start,
        model,
        delivery
      } = t2;
      if (pullRequestId === null) {
        throw new Error("404 The pull request is not loaded yet.");
      }
      const context = await resolveRequestContext();
      const body = prepareSidebarChatRequestBody({
        body: {
          context,
          installationId,
          pullRequestId,
          model
        }
      });
      await sendPageChatMessage({
        sessionId,
        start,
        turnId,
        text,
        delivery,
        model,
        pullRequestId,
        context: body.context ?? context
      });
    };
    $[0] = installationId;
    $[1] = pullRequestId;
    $[2] = resolveRequestContext;
    $[3] = sessionId;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  return useEventCallback(t1);
}

/** The text and model a sidebar send carries, as `useChat`'s `sendMessage` takes them. */
export function sendArguments(...[message, options]: Parameters<SidebarChatEngine['sendMessage']>): {
  text: string;
  model: string | undefined;
} {
  const text = message && 'text' in message && typeof message.text === 'string' ? message.text : '';
  const body = options?.body as {
    model?: unknown;
  } | undefined;
  return {
    text,
    model: typeof body?.model === 'string' ? body.model : undefined
  };
}

/**
 * The sidebar chat's engine on a pull request page's code session: its
 * transcript as the sidebar's messages, sending through the page chat route,
 * and Stop interrupting the agent. A chat with no session yet reads the one its
 * first message starts under the chat's id, so the chat stays the same chat,
 * queued prompts included, as the session starts.
 *
 * The page acts on the page's tools as the sidebar's own chat acted on its
 * tools' calls, for each call that arrives during its turn while the page is
 * open. A call the transcript had when the page opened, or that shows only once
 * its turn is over, as a resumed sandbox's transcript brings it, ran before:
 * the page leaves it as it was.
 */
export function useCodeSessionChat({
  chatId,
  sessionId,
  enabled = true,
  preloading,
  aiContext,
  resolveRequestContext,
  onStarted,
  onToolCall,
  onTurnEnd
}: {
  chatId: string;
  /** The chat's session; null until the chat's first message starts one under `chatId`. */
  sessionId: string | null;
  /** Whether the page chats on its session; when not, the hook reads nothing. */
  enabled?: boolean;
  /**
   * The page preloads the session the chat's first message starts (`useWarmPageChatSession`):
   * before that message, the chat reads it only for its sandbox starting.
   */
  preloading: boolean;
  aiContext: AiContext;
  resolveRequestContext: () => Promise<AiContext>;
  /** The chat's first message started its session. */
  onStarted: (sessionId: string) => void;
  onToolCall: (event: SidebarChatToolCallEvent) => void;
  onTurnEnd: (end: SidebarChatTurnEnd) => void;
}): SidebarChatEngine & {
  gone: boolean;
  historyLoading: boolean;
  retryHistory: (() => void) | null;
  /** The session the chat reads, whose events the page delivers (`CodeSessionSync`); null for none. */
  readSessionId: string | null;
  /** The session's sandbox starting, for the line above the composer; null once it is ready. */
  sandbox: SandboxStartStatus | null;
} {
  const queryClient = useQueryClient();
  const started = sessionId !== null;
  const readId = sessionId ?? chatId;
  // A chat with no session has nothing to read until its first message starts one, except the
  // sandbox of the session the page preloads for it.
  const reads = enabled && (started || preloading);
  const session = useCodeSession(readId, {
    embedded: true,
    enabled: reads
  });
  const send = usePageChatSend({
    sessionId: readId,
    resolveRequestContext
  });
  const [pending, setPending] = useState<PendingPageChatPrompt[]>([]);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState<Error | undefined>(undefined);
  const sessionUrl = typeof window === 'undefined' ? `/code/${readId}` : `${window.location.origin}/code/${readId}`;

  // The page's actions resolve against what the page shows (`resolveFocusCodeResult`,
  // `resolveSetPrFileViewedResult`); the parent rebuilds the context object on every render.
  const {
    codeContent,
    orderedFiles,
    activeFilename,
    visibleFilename
  } = aiContext;
  const transcript = useMemo(() => started ? sessionChatMessages({
    parts: session.parts,
    context: {
      codeContent,
      orderedFiles,
      activeFilename,
      visibleFilename
    },
    sessionUrl
  }) : [], [started, session.parts, codeContent, orderedFiles, activeFilename, visibleFilename, sessionUrl]);
  const loaded = started && session.row !== null;
  const readsHistory = enabled && started && sessionId !== chatId;
  const historyPending = readsHistory && (!loaded || session.resuming);
  const historyFailed = historyPending && session.readError !== null && !session.notFound;
  const retryHistory = useEventCallback(() => {
    void session.refresh();
  });
  const activity = session.session?.activity ?? NO_ACTIVITY;

  // A message the person sent shows until the transcript has its turn, under the id the
  // transcript gives it.
  const {
    messages,
    waitingForTranscript
  } = useMemo(() => {
    const promptIds = new Set(transcript.filter(message => message.role === 'user').map(message_0 => message_0.id));
    const shownPending = pending.filter(prompt => prompt.turnId === null ? promptIds.size === 0 : !promptIds.has(deterministicPromptId(prompt.turnId)));
    return {
      messages: [...transcript, ...shownPending.map((prompt_0): SidebarChatMessage => ({
        id: prompt_0.turnId === null ? 'pending-first' : deterministicPromptId(prompt_0.turnId),
        role: 'user',
        parts: [{
          type: 'text',
          text: prompt_0.text
        }]
      }))],
      waitingForTranscript: shownPending.length > 0
    };
  }, [transcript, pending]);
  const awaitingAnswer = session.forms.length > 0;
  const working = loaded && (session.row?.status === 'creating' || session.isRunning || activity.some(turn => !isTerminalActivity(turn)));
  const status: ChatStatus = error !== undefined ? 'error' : sending || waitingForTranscript ? 'submitted' : working && !awaitingAnswer ? 'streaming' : 'ready';

  // Which page action calls the page acts on, decided once per call when a committed render
  // first shows it. A layout effect runs before the blocks' own effects in the same commit, so a
  // call marked replayed here never scrolls the page or marks files from its block.
  const decidedCallIds = useRef<Map<string, 'live' | 'replayed'> | null>(null);
  useLayoutEffect(() => {
    if (!loaded) {
      return;
    }
    const firstLoad = decidedCallIds.current === null;
    const decided = decidedCallIds.current ??= new Map();
    const turnByPrompt = new Map(activity.map(turn_0 => [deterministicPromptId(turn_0.id), turn_0]));
    const replayed: string[] = [];
    for (const call of pageActionCalls(transcript)) {
      if (decided.has(call.replayKey)) {
        continue;
      }
      const turn_1 = turnByPrompt.get(call.promptId);
      const live = !firstLoad && turn_1 !== undefined && !isTerminalActivity(turn_1);
      decided.set(call.replayKey, live ? 'live' : 'replayed');
      if (live) {
        onToolCall({
          toolCall: {
            toolCallId: call.toolCallId,
            toolName: call.toolName,
            input: call.input,
            replayKey: call.replayKey
          }
        });
      } else {
        replayed.push(call.replayKey);
      }
    }
    markPageActionsReplayed(replayed);
  }, [activity, loaded, onToolCall, transcript]);

  // A turn this page sent ending: what the sidebar's own chat learned from `onFinish`.
  const awaitingTurnEnd = useRef(false);
  const wasBusy = useRef(false);
  useEffect(() => {
    const busy = status === 'submitted' || status === 'streaming' || awaitingAnswer;
    if (wasBusy.current && !busy && awaitingTurnEnd.current) {
      awaitingTurnEnd.current = false;
      onTurnEnd({
        lastMessageId: messages.at(-1)?.id ?? null,
        completed: status === 'ready' && activity.at(-1)?.status === 'done'
      });
    }
    wasBusy.current = busy;
  }, [activity, awaitingAnswer, messages, onTurnEnd, status]);
  const sendMessage = useEventCallback(async (...args: Parameters<SidebarChatEngine['sendMessage']>) => {
    const {
      text,
      model
    } = sendArguments(...args);
    if (text.trim().length === 0) {
      return;
    }
    const turnId = crypto.randomUUID();
    const prompt_1: PendingPageChatPrompt = {
      turnId: started ? turnId : null,
      text
    };
    setError(undefined);
    setSending(true);
    setPending(current => [...current, prompt_1]);
    // Set before the send, so no status change its turn makes can come before it.
    awaitingTurnEnd.current = true;
    try {
      // A message sent while the agent waits on its question answers it.
      await send({
        text,
        turnId,
        start: !started,
        model,
        delivery: awaitingAnswer ? 'steer' : 'queue'
      });
      if (!started) {
        // Clear the pre-creation 404 before selecting the new session; a failed fresh read
        // can still report that the session is gone.
        void queryClient.resetQueries({
          queryKey: codeSessionKeys.detail(readId),
          exact: true
        });
        onStarted(readId);
      }
    } catch (sendError) {
      awaitingTurnEnd.current = false;
      setPending(current_0 => current_0.filter(entry => entry !== prompt_1));
      setError(sendError instanceof Error ? sendError : new Error(String(sendError)));
    } finally {
      setSending(false);
    }
  });

  // Stops the running turn, and the person's turns still waiting to run, as the sidebar's own
  // chat stopped its answer.
  const stop = useEventCallback(async () => {
    if (!working) {
      return;
    }
    const waiting = activity.filter(turn_2 => turn_2.status === 'queued');
    await Promise.all([session.interrupt(), ...waiting.map(turn_3 => session.cancelTurn(turn_3.id))]).catch(() => undefined);
  });
  return {
    messages,
    // The session's transcript is the record: the chat's own edits to it, such as dropping an
    // interrupted turn before Send now, would only hide what the session keeps.
    setMessages: () => undefined,
    sendMessage,
    status,
    stop,
    error,
    clearError: () => setError(undefined),
    gone: enabled && started && (session.notFound || session.session?.archived === true),
    // A session the page goes on with, not one this chat's first message just started, has a
    // transcript to show before the chat takes messages, including while the sandbox resumes it.
    historyLoading: historyPending && !historyFailed,
    retryHistory: historyFailed ? retryHistory : null,
    ...sessionRead(session, {
      reads,
      readId,
      started
    })
  };
}

/**
 * Which session the chat reads, whose events the page delivers, and that session's sandbox
 * starting. Nothing when it reads none, whatever an earlier read left cached.
 */
function sessionRead(session: Pick<UseCodeSessionResult, 'boot' | 'row' | 'resuming'>, {
  reads,
  readId,
  started
}: {
  reads: boolean;
  readId: string;
  started: boolean;
}): {
  readSessionId: string | null;
  sandbox: SandboxStartStatus | null;
} {
  if (!reads) {
    return {
      readSessionId: null,
      sandbox: null
    };
  }
  return {
    readSessionId: readId,
    sandbox: sandboxStartStatus({
      boot: session.boot,
      row: session.row,
      resuming: session.resuming,
      // Only before the first message: the page's warm flag stays set for the chat, so after it
      // a later turn's agent check would read as the preload starting.
      preloading: !started
    })
  };
}

/**
 * The page's actions among the transcript's tool calls, as `useChat`'s `onToolCall` names them,
 * with the prompt whose turn each answers.
 */
function pageActionCalls(messages: readonly SidebarChatMessage[]) {
  const actionNames: ReadonlySet<string> = new Set(PAGE_ACTION_TOOL_NAMES);
  return messages.flatMap(message => {
    // A reply's id is its prompt's, with `:reply` (`sessionChatMessages`).
    const promptId = message.id.replace(/:reply$/, '');
    return message.parts.flatMap(part => {
      const toolName = part.type.startsWith('tool-') ? part.type.slice('tool-'.length) : null;
      return toolName !== null && actionNames.has(toolName) && 'toolCallId' in part && 'state' in part && (part.state === 'input-available' || part.state === 'output-available') ? [{
        toolCallId: part.toolCallId,
        replayKey: pageActionReplayKey(message.id, part.toolCallId),
        toolName,
        input: part.input,
        promptId
      }] : [];
    });
  });
}